import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Table, InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useResizeColumns,
  useFlexLayout,
  useFilters,
} from 'react-table';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;

const Header = tw.div`flex justify-between items-center mb-4`;
const Title = tw.h2`text-gray-800 font-semibold text-xl`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 11px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}
      
      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
      text-align: right; /* Align numeric cells to the right */
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const RefreshButton = styled(Button)`
  ${tw`ml-2`}
`;

const BoxContainer = styled.div`
  ${tw`flex items-center`}
`;

const Box = styled.div`
  ${tw`p-2 rounded ml-2`}
  ${({ color }) => color === 'gray' && tw`bg-gray-200 text-gray-800`}
  ${({ color }) => color === 'green' && tw`bg-green-200 text-green-800`}
  ${({ color }) => color === 'red' && tw`bg-red-200 text-red-800`}
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const FinNifty = () => {
  const [bankNiftyData, setBankNiftyData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const apiUrl = 'https://falcon-algo.onrender.com/api/summary/markets'; // Update with your backend URL
      const priceSymbolList = [
        "HDFCBANK","ICICIBANK","SBIN","BAJFINANCE","AXISBANK","KOTAKBANK","BAJAJFINSV","PFC","SBILIFE","RECLTD","HDFCLIFE","CHOLAFIN","SHRIRAMFIN","HDFCAMC","ICICIGI","ICICIPRULI","SBICARD","MUTHOOTFIN","LICHSGFIN","IDFC" ];
  
      const bodyData = {
        exchangeAggReqMap: {
          NSE: {
            priceSymbolList: priceSymbolList,
            indexSymbolList: []
          },
          BSE: {
            priceSymbolList: [],
            indexSymbolList: []
          }
        }
      };
  
      // console.log('Sending bodyData:', bodyData); // Log the data being sent
  
      const response = await axios.post(apiUrl, bodyData);
  
      if (response.data && response.data.exchangeAggRespMap && response.data.exchangeAggRespMap.NSE && response.data.exchangeAggRespMap.NSE.priceLivePointsMap) {
        const priceLivePointsMap = response.data.exchangeAggRespMap.NSE.priceLivePointsMap;
        const bankNiftyArray = Object.keys(priceLivePointsMap).map(key => priceLivePointsMap[key]);
  
        setBankNiftyData(bankNiftyArray);
      } else {
        console.error('Unexpected API response:', response.data);
        setBankNiftyData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(
    () => [
      { 
        Header: 'Logo', 
        accessor: 'logo', 
        id: 'logo',
        width: 60,
        Cell: ({ row }) => (
          <img
            src={`https://s3tv-symbol.dhan.co/symbols/${row.original.symbol}.svg`}
            alt={row.original.symbol}
            style={{ width: 30, height: 30 }}
          />
        )
      },
      { Header: 'Symbol', accessor: 'symbol', id: 'symbol', width: 120 },
      { Header: 'Open', accessor: 'open', width: 80 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'High', accessor: 'high', width: 80 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Low', accessor: 'low', width: 80 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Close', accessor: 'close', width: 80 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Last Traded Price', accessor: 'ltp', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Volume', accessor: 'volume', width: 90,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Timestamp', accessor: 'tsInMillis', Cell: ({ cell: { value } }) => new Date(value).toLocaleString(), width: 130 },
      { Header: 'Low Price Range', accessor: 'lowPriceRange', width: 120 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'High Price Range', accessor: 'highPriceRange', width: 120 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Total Buy Qty', accessor: 'totalBuyQty', width: 120 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Total Sell Qty', accessor: 'totalSellQty', width: 120 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Change', accessor: 'dayChange', width: 90 , Cell: ({ cell: { value } }) => <span style={{ color: value >= 0 ? 'green' : 'red' }}>{value !== null ? value.toFixed(2) : '0.00'}</span>, width: 90 ,align: 'right'},
      { Header: 'Change %', accessor: 'dayChangePerc', Cell: ({ cell: { value } }) => <span style={{ color: value >= 0 ? 'green' : 'red' }}>{value !== null ? value.toFixed(2) : '0.00'}%</span>, width: 90 ,align: 'right'},
      { Header: 'Open Interest', accessor: 'openInterest', width: 100 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Last Trade Qty', accessor: 'lastTradeQty', width: 120 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Last Trade Time', accessor: 'lastTradeTime', Cell: ({ cell: { value } }) => new Date(value).toLocaleString(), width: 130 ,align: 'right'},
      { Header: 'Prev Open Interest', accessor: 'prevOpenInterest', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'OI Day Change', accessor: 'oiDayChange', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'OI Day Change %', accessor: 'oiDayChangePerc', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'Low Trade Range', accessor: 'lowTradeRange', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
      { Header: 'High Trade Range', accessor: 'highTradeRange', width: 130 ,align: 'right', Cell: ({ value }) => (value !== null ? value.toFixed(2) : '0.00') },
    ],
    []
  ); 
 

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    setGlobalFilter,
    setPageSize,
  } = useTable(
    {
      columns,
      data: bankNiftyData,
      initialState: { pageIndex: 0, pageSize: 50, globalFilter: '' },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useResizeColumns,
    useFlexLayout
  );

  useEffect(() => {
    setPageSize(50); // Ensure the page size is set to 50 to show all companies
  }, [setPageSize]);

  // Calculate total count, count of positive changes, and count of negative changes
  const totalCount = bankNiftyData.length;
  const totalUpCount = bankNiftyData.filter(row => row.dayChange >= 0).length;
  const totalDownCount = bankNiftyData.filter(row => row.dayChange < 0).length;

  return (
    <Container>
      <Header>
        <Title>FinNifty</Title>
        <RefreshButton onClick={fetchData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </Header>

      <BoxContainer className="mb-3">
        <Box color="gray">Total: {totalCount}</Box>
        <Box color="green">Up: {totalUpCount}</Box>
        <Box color="red">Down: {totalDownCount}</Box>
      </BoxContainer>

      <InputGroup className="mb-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
          </Spinner>
            <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <TableWrapper>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <ResizableHeader
                      key={column.id}
                      column={column}
                      className="text-center"
                    />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}
    </Container>
  );
};

export default FinNifty;

